import cn from "classnames"
import { Section as SectionType, Field as FieldType } from "@/models/new/forms.types"
import { FormResponse, ResponseValue } from "@/models/new/formResponse.types"
import { SelectOption } from "@/types/select-options"
import { ScopedOrganizationEntity } from "@/models/scoped-organization-hierarchy"
import { ConditionValueAliases, shouldShowElement } from "@/utils/conditionalDisplay"

import Field from "./Field"
import FieldMediaNavigationWrapper from "./FieldMediaNavigationWrapper"
import { isFieldMediaEligible } from "@/components/shared/attachments/attachment-groups"
import type { ReportCreationMedia } from "./report-creation-media"

interface Props {
  nested?: boolean
  section: SectionType
  formResponse: FormResponse
  onChange?: (fieldId: string, value: ResponseValue) => void
  onDynamicGroupInstanceRemove?: (
    field: Extract<FieldType, { type: "dynamicGroup" }>,
    instance: number,
  ) => void
  onDynamicGroupInstancesChange?: (groupId: string, instances: number[] | null) => void
  listOptions?: Record<string, SelectOption[]>
  conditionValueAliases?: ConditionValueAliases
  organizations?: ScopedOrganizationEntity[]
  excludedFieldTypes?: readonly string[]
  fieldMediaLocators?: Set<string>
  selectedFieldMediaLocator?: string | null
  canCreateFieldMedia?: boolean
  onNavigateToFieldMedia?: (locator: string, options?: { openComposer?: boolean }) => void
  isCreating?: boolean
  creationMedia?: ReportCreationMedia
  onCreationMediaChange?: (media: ReportCreationMedia) => void
}

const Section = (props: Props) => {
  const { section, formResponse, onChange } = props

  const handleChangeField = (fieldId: string, value: ResponseValue) => {
    onChange?.(fieldId, value)
  }

  const filteredFields = section.fields.filter(
    (field) => !props.excludedFieldTypes?.includes(field.type),
  )
  const filteredFieldIds = new Set(filteredFields.map((field) => field.id))

  // Group conditional fields by trigger field ID
  const conditionalGroups = filteredFields.reduce(
    (acc, field) => {
      if (
        field.conditionalDisplay?.triggerField &&
        filteredFieldIds.has(field.conditionalDisplay.triggerField)
      ) {
        const triggerId = field.conditionalDisplay.triggerField
        if (!acc[triggerId]) {
          acc[triggerId] = []
        }
        acc[triggerId].push(field)
      }
      return acc
    },
    {} as Record<string, FieldType[]>,
  )

  // Track which fields are conditional (to skip when rendering main list)
  const conditionalFieldIds = new Set(
    Object.values(conditionalGroups)
      .flat()
      .map((f) => f.id),
  )

  // Render a field and, recursively, any of its own conditional children that should
  // be visible. Arbitrary-depth chains (A -> B -> C -> ...) render nested beneath
  // their respective triggers; a conditional field that is itself a trigger still
  // draws its own dependents because we look up `conditionalGroups[field.id]` here
  // rather than only iterating the top-level field's immediate children.
  const renderField = (field: FieldType) => {
    const children = conditionalGroups[field.id] ?? []
    const pendingFieldMedia = props.creationMedia?.fieldMedia[field.id] || {
      target: {
        fieldId: field.id,
        fieldName: field.name,
        fieldLabel: field.label,
      },
      uploads: [],
    }

    return (
      <div key={field.id} className="w-full">
        <FieldMediaNavigationWrapper
          locator={field.id}
          hasFieldMedia={Boolean(props.fieldMediaLocators?.has(field.id))}
          canCreateFieldMedia={Boolean(props.canCreateFieldMedia && isFieldMediaEligible(field))}
          isSelected={props.selectedFieldMediaLocator === field.id}
          onNavigateToFieldMedia={props.onNavigateToFieldMedia}
          pendingFieldMedia={pendingFieldMedia}
          onPendingFieldMediaChange={
            props.creationMedia && props.onCreationMediaChange && isFieldMediaEligible(field)
              ? (bundle) => {
                  const fieldMedia = { ...props.creationMedia!.fieldMedia }
                  if (bundle) fieldMedia[field.id] = bundle
                  else delete fieldMedia[field.id]
                  props.onCreationMediaChange!({ ...props.creationMedia!, fieldMedia })
                }
              : undefined
          }
        >
          <Field
            field={field}
            value={formResponse.responses[field.id]}
            onChange={handleChangeField}
            onDynamicGroupInstanceRemove={props.onDynamicGroupInstanceRemove}
            onDynamicGroupInstancesChange={props.onDynamicGroupInstancesChange}
            listOptions={props.listOptions}
            conditionValueAliases={props.conditionValueAliases}
            organizations={props.organizations}
            formResponse={formResponse}
            fieldMediaLocators={props.fieldMediaLocators}
            selectedFieldMediaLocator={props.selectedFieldMediaLocator}
            canCreateFieldMedia={props.canCreateFieldMedia}
            onNavigateToFieldMedia={props.onNavigateToFieldMedia}
            isCreating={props.isCreating}
            pendingUploads={props.creationMedia?.responseUploads[field.id]}
            onPendingUploadsChange={
              props.creationMedia && props.onCreationMediaChange
                ? (uploads) => {
                    const responseUploads = { ...props.creationMedia!.responseUploads }
                    if (uploads.length > 0) responseUploads[field.id] = uploads
                    else delete responseUploads[field.id]
                    props.onCreationMediaChange!({
                      ...props.creationMedia!,
                      responseUploads,
                    })
                  }
                : undefined
            }
            creationMedia={props.creationMedia}
            onCreationMediaChange={props.onCreationMediaChange}
          />
        </FieldMediaNavigationWrapper>
        {children.length > 0 && (
          <div className="w-full border-l-4 border-blue-300 pl-4 ml-4 my-2">
            {children
              .filter((conditionalField) =>
                shouldShowElement(
                  conditionalField.conditionalDisplay,
                  formResponse,
                  undefined,
                  props.conditionValueAliases,
                ),
              )
              .map((conditionalField) => renderField(conditionalField))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn("w-full", { "flex flex-row flex-wrap": !props.nested })}>
      {filteredFields.map((field) => {
        // Skip if this is a conditional field (will be rendered with its trigger)
        if (conditionalFieldIds.has(field.id)) return null

        // Skip if field doesn't meet conditional display criteria
        if (
          !shouldShowElement(
            field.conditionalDisplay,
            formResponse,
            undefined,
            props.conditionValueAliases,
          )
        )
          return null

        return renderField(field)
      })}
    </div>
  )
}

export default Section
